import type { CustomerSummary, Reseller, ResellerSummary, StaffUser } from '@central/api';
import {
  Card,
  KpiTile,
  StatusChip,
  color,
  formatAgo,
  formatDate,
  formatMonth,
  initials,
  status,
} from '@central/ui';
import { Alert, Button, Group, Modal, Select, Stack, Table, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type CSSProperties, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { Crumbs, DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import { StaffSsoProviders } from '../components/StaffSsoProviders';
import { errorMessage } from '../lib/errors';
import { customerStatusChip, mono, muted } from '../lib/labels';
import { useCanWrite, useMe } from '../lib/me';
import { EditReseller, LogoImage } from './Resellers';
import { Invite } from './Staff';

const tiles: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 12,
};

/**
 * One reseller, for Rethink staff: what it looks after (customers,
 * environments, support tickets) and who works for it. Owners and Admins
 * assign customers to it; Owners invite its users.
 */
export function ResellerDetail() {
  const api = useApi();
  const { resellerId } = useParams({ from: '/app/resellers/$resellerId' });
  const reseller = useQuery({
    queryKey: keys.reseller(resellerId),
    queryFn: () => api.resellers.get(resellerId),
  });
  return (
    <>
      <Crumbs
        items={[{ label: 'Resellers', to: '/resellers' }, { label: reseller.data?.name ?? '…' }]}
      />
      <QueryState isPending={reseller.isPending} error={reseller.error}>
        {() => <Body r={reseller.data!} />}
      </QueryState>
    </>
  );
}

function Body({ r }: { r: Reseller }) {
  const api = useApi();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  const summary = useQuery({
    queryKey: keys.resellerSummary(r.id),
    queryFn: () => api.resellers.summary(r.id),
  });
  return (
    <>
      <header
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
          {r.logoUrl ? (
            <div
              style={{
                flex: 'none',
                border: `1px solid ${color.border}`,
                borderRadius: 10,
                padding: 4,
                lineHeight: 0,
                background: color.surface,
              }}
            >
              <LogoImage src={r.logoUrl} name={r.name} size={40} />
            </div>
          ) : (
            <div
              aria-hidden
              data-testid="reseller-initials"
              style={{
                width: 48,
                height: 48,
                flex: 'none',
                borderRadius: 10,
                background: color.link,
                color: color.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
              }}
            >
              {initials(r.name)}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: '34px' }}>{r.name}</h1>
            <div style={{ fontSize: 14, ...muted }}>
              Reseller since {formatMonth(r.createdAt)} · gives tier 1 support to its customers and
              escalates to Rethink
            </div>
            <Contact r={r} />
          </div>
        </div>
        {canWrite && (
          <Button variant="default" h={40} onClick={() => setEditing(true)}>
            Edit reseller
          </Button>
        )}
      </header>
      {canWrite && editing && <EditReseller r={r} onClose={() => setEditing(false)} />}
      <QueryState isPending={summary.isPending} error={summary.error}>
        {() => <SummaryTiles s={summary.data!} />}
      </QueryState>
      <Customers r={r} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        <Team r={r} summary={summary.data} />
        <Support resellerId={r.id} summary={summary.data} />
      </div>
      <ResellerSso resellerId={r.id} />
    </>
  );
}

/** The support contact the reseller's customers see in the portal; nothing when none is set. */
function Contact({ r }: { r: Reseller }) {
  const parts = [
    r.supportEmail && <a href={`mailto:${r.supportEmail}`}>{r.supportEmail}</a>,
    r.supportPhone && <span>{r.supportPhone}</span>,
    r.website && (
      <a href={r.website} target="_blank" rel="noopener noreferrer">
        {r.website}
      </a>
    ),
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div
      aria-label="Support contact"
      style={{ fontSize: 14, display: 'flex', gap: '4px 16px', flexWrap: 'wrap', marginTop: 2 }}
    >
      {parts.map((p, i) => (
        <span key={i}>{p}</span>
      ))}
    </div>
  );
}

function SummaryTiles({ s }: { s: ResellerSummary }) {
  const notUp = s.environments.byHealth.down + s.environments.byHealth.degraded;
  return (
    <ul aria-label="Totals" style={tiles}>
      <li>
        <KpiTile label="Customers" value={s.customers.total} />
      </li>
      <li>
        <KpiTile label="Active customers" value={s.customers.active} />
      </li>
      <li>
        <KpiTile label="Environments" value={s.environments.total} />
      </li>
      <li>
        <KpiTile
          label="Down or degraded"
          value={notUp}
          valueColor={notUp > 0 ? status.down.fg : undefined}
        />
      </li>
      <li>
        <KpiTile label="Open tier 1 tickets" value={s.tickets.openTier1} />
      </li>
      <li>
        <KpiTile label="Escalated to Rethink" value={s.tickets.escalated} />
      </li>
    </ul>
  );
}

/** The reseller's customers; Owners and Admins assign and unassign them. */
function Customers({ r }: { r: Reseller }) {
  const api = useApi();
  const canWrite = useCanWrite();
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState<CustomerSummary>();
  const list = useInfiniteQuery({
    queryKey: keys.customerList('', '', r.id),
    queryFn: ({ pageParam }) => api.customers.list({ resellerId: r.id, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card
      title="Customers"
      padded={false}
      action={
        <Group gap={12}>
          <Link to="/customers" search={{ resellerId: r.id }} style={{ fontSize: 13 }}>
            Open in Customers
          </Link>
          {canWrite && (
            <Button size="xs" variant="default" onClick={() => setAssigning(true)}>
              Assign customer
            </Button>
          )}
        </Group>
      }
    >
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No customers yet. Rethink supports every customer not assigned here.</Empty>
          ) : (
            <DataTable
              label={`${r.name} customers`}
              minWidth={720}
              head={[
                'Customer',
                'Status',
                'Licenses',
                'Next license expiry',
                'Environments',
                ...(canWrite ? [''] : []),
              ]}
            >
              {items.map((c) => {
                const chip = customerStatusChip[c.status];
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td style={{ fontWeight: 600 }}>
                      <Link to="/customers/$customerId" params={{ customerId: c.id }}>
                        {c.name}
                      </Link>
                    </Table.Td>
                    <Table.Td>
                      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                    </Table.Td>
                    <Table.Td style={mono}>{c.licenseCount}</Table.Td>
                    <Table.Td>{formatDate(c.nextLicenseExpiry)}</Table.Td>
                    <Table.Td style={mono}>{c.environmentCount}</Table.Td>
                    {canWrite && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          aria-label={`Unassign ${c.name}`}
                          onClick={() => setUnassigning(c)}
                        >
                          Unassign
                        </Button>
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </DataTable>
          )
        }
      </QueryState>
      {list.hasNextPage && (
        <LoadMore
          what="customers"
          loading={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        />
      )}
      {assigning && <AssignCustomer r={r} onClose={() => setAssigning(false)} />}
      {unassigning && (
        <UnassignCustomer r={r} c={unassigning} onClose={() => setUnassigning(undefined)} />
      )}
    </Card>
  );
}

/** Sets or clears a customer's reseller, then refreshes the lists and counts that move with it. */
function useSetReseller(onDone: () => void) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resellerId }: { id: string; resellerId: string }) =>
      api.customers.update(id, { resellerId }),
    onSuccess: async (next) => {
      qc.setQueryData(keys.customer(next.id), next);
      await qc.invalidateQueries({ queryKey: keys.customers });
      await qc.invalidateQueries({ queryKey: keys.resellers });
      onDone();
    },
  });
}

/** Picks a customer Rethink supports directly and assigns it to the reseller. */
function AssignCustomer({ r, onClose }: { r: Reseller; onClose: () => void }) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [query] = useDebouncedValue(search.trim(), 250);
  const [picked, setPicked] = useState<string | null>(null);
  const [problem, setProblem] = useState<string>();
  const direct = useQuery({
    queryKey: keys.customerList(query, '', 'none'),
    queryFn: () => api.customers.list({ query: query || undefined, resellerId: 'none', limit: 20 }),
  });
  const assign = useSetReseller(onClose);
  const options = (direct.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!picked) return setProblem('Choose a customer.');
    setProblem(undefined);
    assign.mutate({ id: picked, resellerId: r.id });
  };
  return (
    <Modal opened onClose={onClose} title={`Assign a customer to ${r.name}`}>
      <form onSubmit={submit} noValidate>
        <Stack gap={14}>
          <Select
            label="Customer"
            description="Customers Rethink supports directly. To move one from another reseller, edit it on its page."
            placeholder={direct.isPending ? 'Loading…' : 'Search customers'}
            data-autofocus
            searchable
            searchValue={search}
            onSearchChange={setSearch}
            filter={({ options }) => options}
            data={options}
            value={picked}
            onChange={setPicked}
            error={problem}
            nothingFoundMessage={direct.isPending ? 'Loading…' : 'No direct customers match'}
          />
          <Text size="sm" style={muted}>
            {r.name} will see the customer, its dedicated environments and its support conversations
            at once, but never its licenses.
          </Text>
          {assign.error && <Alert color="red">{errorMessage(assign.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="dark" loading={assign.isPending}>
              Assign customer
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function UnassignCustomer({
  r,
  c,
  onClose,
}: {
  r: Reseller;
  c: CustomerSummary;
  onClose: () => void;
}) {
  const unassign = useSetReseller(onClose);
  return (
    <Modal opened onClose={onClose} title={`Unassign ${c.name}?`}>
      <Stack gap={14}>
        <Text size="sm">
          Rethink will support {c.name} directly. {r.name} stops seeing the customer, its
          environments and its support conversations at once.
        </Text>
        {unassign.error && <Alert color="red">{errorMessage(unassign.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={unassign.isPending}
            onClick={() => unassign.mutate({ id: c.id, resellerId: '' })}
          >
            Unassign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** "Disabled", "Pending setup" or "Enabled". */
function userStatus(u: StaffUser) {
  if (!u.enabled) return <StatusChip tone="unk">Disabled</StatusChip>;
  if (u.setupPending) return <StatusChip tone="warn">Pending setup</StatusChip>;
  return <StatusChip tone="ok">Enabled</StatusChip>;
}

/**
 * The reseller's console users. Owners and Admins see the list (as on Staff
 * & access); Support sees the counts. Owners invite.
 */
function Team({ r, summary }: { r: Reseller; summary?: ResellerSummary }) {
  const api = useApi();
  const role = useMe().user.role;
  const canList = role === 'Owner' || role === 'Admin';
  const [inviting, setInviting] = useState(false);
  const users = useQuery({
    queryKey: keys.resellerStaff(r.id),
    queryFn: () => api.staff.list({ resellerId: r.id }),
    enabled: canList,
  });
  const items = users.data?.items ?? [];
  const team = summary?.team;
  return (
    <Card
      title="Team"
      padded={false}
      style={{ flex: '2 1 420px' }}
      action={
        <Group gap={12}>
          {canList && (
            <Link to="/staff" style={{ fontSize: 13 }}>
              Manage on Staff & access
            </Link>
          )}
          {role === 'Owner' && (
            <Button size="xs" variant="default" onClick={() => setInviting(true)}>
              Invite reseller user
            </Button>
          )}
        </Group>
      }
    >
      {canList ? (
        <QueryState isPending={users.isPending} error={users.error}>
          {() =>
            items.length === 0 ? (
              <Empty>No users yet.</Empty>
            ) : (
              <DataTable
                label={`${r.name} team`}
                minWidth={560}
                head={['Name', 'Role', 'Status', 'Last sign-in']}
              >
                {items.map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      <div style={{ fontSize: 13, ...muted }}>{u.email}</div>
                    </Table.Td>
                    <Table.Td>
                      {u.resellerAdmin ? (
                        <StatusChip tone="vio">Admin</StatusChip>
                      ) : (
                        <StatusChip tone="blu">Engineer</StatusChip>
                      )}
                    </Table.Td>
                    <Table.Td>{userStatus(u)}</Table.Td>
                    <Table.Td style={muted}>
                      {u.lastSignInAt ? formatAgo(u.lastSignInAt) : 'Never'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      ) : (
        <p style={{ margin: 0, padding: '16px 20px', fontSize: 14 }}>
          {team
            ? `${team.users} ${team.users === 1 ? 'user' : 'users'}, ${team.admins} ${
                team.admins === 1 ? 'admin' : 'admins'
              }, ${team.pendingSetup} pending setup`
            : '…'}
        </p>
      )}
      {role === 'Owner' && (
        <Invite opened={inviting} onClose={() => setInviting(false)} reseller={r.id} />
      )}
    </Card>
  );
}

/**
 * Open tier 1 and escalated ticket counts of the reseller's customers, each
 * linking to the inbox's open tickets of those customers at that tier.
 */
function Support({ resellerId, summary }: { resellerId: string; summary?: ResellerSummary }) {
  const t = summary?.tickets;
  const line: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderTop: `1px solid ${color.rowRule}`,
    fontSize: 14,
    color: color.text,
    textDecoration: 'none',
  };
  return (
    <Card title="Support" padded={false} style={{ flex: '1 1 260px' }}>
      <Link
        to="/inbox"
        search={{ resellerId, tier: 'tier1' }}
        style={{ ...line, borderTop: 'none' }}
      >
        <span>Open with the reseller (tier 1)</span>
        <b>{t ? t.openTier1 : '…'}</b>
      </Link>
      <Link to="/inbox" search={{ resellerId, tier: 'tier2' }} style={line}>
        <span>Escalated to Rethink (tier 2)</span>
        <b>{t ? t.escalated : '…'}</b>
      </Link>
    </Card>
  );
}

/**
 * The reseller's console sign-in (SSO): the identity providers its
 * engineers sign in through. Owners add and change them, Admins read them;
 * Support does not see the section (the server answers it 403).
 */
function ResellerSso({ resellerId }: { resellerId: string }) {
  const role = useMe().user.role;
  if (role !== 'Owner' && role !== 'Admin') return null;
  return <StaffSsoProviders resellerId={resellerId} title="Engineers’ single sign-on" />;
}
